/**
 * Status Badge Component
 * Displays workflow status with color coding
 */

import React from 'react';
import { STATUS_COLORS, WORKFLOW_STATUS_LABELS } from '../../utils/constants';

const colorClasses = {
  gray: 'bg-gray-100 text-gray-800 border-gray-300',
  blue: 'bg-blue-100 text-blue-800 border-blue-300',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  green: 'bg-green-100 text-green-800 border-green-300',
  red: 'bg-red-100 text-red-800 border-red-300',
  purple: 'bg-purple-100 text-purple-800 border-purple-300',
  orange: 'bg-orange-100 text-orange-800 border-orange-300'
};

export default function StatusBadge({ status, size = 'md', className = '', labels, colors }) {
  if (!status) return null;

  // Use custom labels/colors if provided, otherwise fall back to defaults
  const labelMap = labels || WORKFLOW_STATUS_LABELS;
  const colorMap = colors || STATUS_COLORS;

  const label = labelMap[status] || status;
  const color = colorMap[status] || 'gray';
  const colorClass = colorClasses[color] || colorClasses.gray;

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-0.5',
    lg: 'text-base px-3 py-1'
  };

  const sizeClass = sizeClasses[size] || sizeClasses.md;

  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${colorClass} ${sizeClass} ${className}`}
      title={label}
    >
      {label}
    </span>
  );
}

/**
 * Phase Badge Component
 * Displays project phase with color coding
 */
export function PhaseBadge({ phase, className = '' }) {
  if (!phase) return null;

  const phaseColors = {
    sales: 'bg-gray-100 text-gray-800',
    measurement: 'bg-blue-100 text-blue-800',
    '2d': 'bg-blue-100 text-blue-800',
    '3d': 'bg-purple-100 text-purple-800',
    dd: 'bg-yellow-100 text-yellow-800',
    quotation: 'bg-yellow-100 text-yellow-800',
    contract: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800'
  };

  const phaseLabels = {
    sales: 'Sales',
    measurement: 'Measurement',
    '2d': '2D Drawing',
    '3d': '3D',
    dd: 'DD Development',
    quotation: 'Quotation',
    contract: 'Contract',
    closed: 'Closed'
  };

  const colorClass = phaseColors[phase] || phaseColors.sales;
  const label = phaseLabels[phase] || phase;

  return (
    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${colorClass} ${className}`}>
      {label}
    </span>
  );
}

/**
 * Task Status Badge Component
 */
export function TaskStatusBadge({ status, className = '' }) {
  if (!status) return null;

  const taskStatusColors = {
    pending_assignment: 'bg-gray-100 text-gray-800',
    assigned: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    submitted: 'bg-purple-100 text-purple-800',
    approved: 'bg-green-100 text-green-800',
    amendment_required: 'bg-orange-100 text-orange-800'
  };

  const taskStatusLabels = {
    pending_assignment: 'Pending Assignment',
    assigned: 'Assigned',
    in_progress: 'In Progress',
    submitted: 'Submitted',
    approved: 'Approved',
    amendment_required: 'Amendment Required'
  };

  const colorClass = taskStatusColors[status] || taskStatusColors.pending_assignment;
  const label = taskStatusLabels[status] || status;

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colorClass} ${className}`}>
      {label}
    </span>
  );
}
